import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ModalRoot,
  ModalPortal,
  ModalOverlay,
  ModalContent,
  ModalContentHeader,
  ModalContentBody,
  ModalContentFooter,
  ModalContentActions,
  ModalCancelButton,
  ModalActionButton,
  TextInput,
} from '@socialchorus/shared-ui-components';

export const LINK_TEXT_MAX_LENGTH = 100;

export interface AddLinkValues {
  text: string;
  url: string;
}

interface AddLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: AddLinkValues) => void;
  initialText?: string;
  initialUrl?: string;
}

export default function AddLinkModal({
  open,
  onOpenChange,
  onSubmit,
  initialText = '',
  initialUrl = '',
}: AddLinkModalProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');

  // Seed the fields from the current selection each time the modal is opened.
  useEffect(() => {
    if (open) {
      setText(initialText);
      setUrl(initialUrl);
    }
  }, [open, initialText, initialUrl]);

  const trimmedUrl = url.trim();
  const canSubmit = trimmedUrl.length > 0;

  // When the link text is already known (editing an existing link, or
  // linkifying a selection), start the user in the URL field instead.
  const focusUrlField = initialText.trim().length > 0;
  // A pre-populated URL means we are editing an existing link.
  const isEditing = initialUrl.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ text: text.trim(), url: trimmedUrl });
    onOpenChange(false);
  };

  return (
    <ModalRoot open={open} onOpenChange={onOpenChange} mode="dialog">
      <ModalPortal>
        <ModalOverlay />
        <ModalContent size="small">
          <ModalContentHeader
            title={t('user_generated_content.editor.add_link.title')}
          />
          <ModalContentBody>
            <TextInput
              label={t('user_generated_content.editor.add_link.text_label')}
              placeholder={t(
                'user_generated_content.editor.add_link.text_placeholder'
              )}
              value={text}
              onChange={setText}
              maxLength={LINK_TEXT_MAX_LENGTH}
              showCharacterCounter
              fullWidth
              // Focusing a field when the dialog opens is intentional UX.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={!focusUrlField}
            />
            <TextInput
              label={t('user_generated_content.editor.add_link.url_label')}
              placeholder={t(
                'user_generated_content.editor.add_link.url_placeholder'
              )}
              type="url"
              value={url}
              onChange={setUrl}
              showCharacterCounter={false}
              fullWidth
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={focusUrlField}
            />
          </ModalContentBody>
          <ModalContentFooter>
            <ModalContentActions>
              <ModalCancelButton label={t('common.cancel')} />
              <ModalActionButton
                label={isEditing ? t('common.apply') : t('common.add')}
                onClick={handleSubmit}
                disabled={!canSubmit}
              />
            </ModalContentActions>
          </ModalContentFooter>
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
}
